import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import React, { useState } from 'react';
import styled from 'styled-components';

const Card = styled.div`
  background-color: ${(props) => props.theme.colors.white};
  border-radius: ${(props) => props.theme.border.default};
  box-shadow: ${(props) => props.theme.shadow.default};
  padding: 1.25rem 1.5rem;
  margin: 0 0 1rem 0;
  box-sizing: border-box;
  width: 100%;
`;

const Header = styled.button`
  width: 100%;
  background: transparent;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  color: ${(props) => props.theme.colors.mainColor};

  & svg {
    font-size: 1.5rem;
  }
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Title = styled.h2`
  font-size: ${(props) => props.theme.fontSize.big};
  color: ${(props) => props.theme.colors.mainColor};
  font-weight: 700;
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${(props) => props.theme.fontSize.small};
  color: ${(props) => props.theme.colors.lightGray};
  margin: 0.5rem 0 0 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(18rem, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
`;

// Cada item e um mini-card com codigo em cima e descricao embaixo. Empilhar
// (em vez de inline) mantem o alinhamento uniforme mesmo com codigos de
// larguras bem diferentes (TARIFA x TARIFA_ABERTURA_CONTA_CONSIGNADA).
const Item = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.6rem 0.75rem;
  background-color: ${(props) => props.theme.colors.background};
  border-radius: ${(props) => props.theme.border.default};
`;

const Code = styled.code`
  align-self: flex-start;
  max-width: 100%;
  font-family: 'Courier New', Courier, monospace;
  font-size: ${(props) => props.theme.fontSize.small};
  font-weight: 700;
  color: ${(props) => props.theme.colors.white};
  background-color: ${(props) => props.theme.colors.mainColor};
  padding: 0.15rem 0.45rem;
  border-radius: ${(props) => props.theme.border.min};
  word-break: break-all;
`;

const Description = styled.span`
  font-size: ${(props) => props.theme.fontSize.small};
  color: ${(props) => props.theme.colors.gray};
  line-height: 1.4;
`;

interface Legenda {
  codigo: string;
  descricao: string;
}

// Dicionario de codigos de tarifa/taxa que aparecem na coluna TIPO/COD_MSG
// dos relatorios. Ordenado alfabeticamente pra leitura rapida.
const LEGENDAS: Legenda[] = [
  { codigo: 'TARIFA', descricao: 'Tarifa genérica' },
  {
    codigo: 'TARIFA_ABERTURA_CONTA_CONSIGNADA',
    descricao: 'Tarifa de abertura de conta consignada',
  },
  {
    codigo: 'TARIFA_ABERTURA_CONTA_ESCROW',
    descricao: 'Tarifa de abertura de conta escrow',
  },
  {
    codigo: 'TARIFA_ABERTURA_CONTA_PF',
    descricao: 'Tarifa de abertura de conta movimento de pessoa física',
  },
  {
    codigo: 'TARIFA_ABERTURA_CONTA_PJ',
    descricao: 'Tarifa de abertura de conta movimento de pessoa jurídica',
  },
  {
    codigo: 'TARIFA_ALTERACAO_BOLETO',
    descricao: 'Tarifa de alteração de boleto',
  },
  { codigo: 'TARIFA_BAIXADO', descricao: 'Tarifa de boleto baixado' },
  { codigo: 'TARIFA_BOLETO', descricao: 'Tarifa de boleto liquidado' },
  {
    codigo: 'TARIFA_BOLETO_VENCIDO',
    descricao: 'Tarifa de boleto vencido',
  },
  { codigo: 'TARIFA_CARTAO_SAQUE', descricao: 'Tarifa de saque via cartão' },
  {
    codigo: 'TARIFA_CARTAO_SOLICITACAO',
    descricao: 'Tarifa de solicitação de cartão',
  },
  { codigo: 'TARIFA_CRIPTO_COMPRA', descricao: 'Tarifa de compra de cripto' },
  { codigo: 'TARIFA_CRIPTO_VENDA', descricao: 'Tarifa de venda de cripto' },
  { codigo: 'TARIFA_MENSAL', descricao: 'Tarifa de mensalidade da conta' },
  { codigo: 'TARIFA_PIX', descricao: 'Tarifa de envio de Pix' },
  {
    codigo: 'TARIFA_PIX_GERAR_QRCODE',
    descricao: 'Tarifa de geração de QrCode',
  },
  {
    codigo: 'TARIFA_RECEBIMENTO_BOLETO',
    descricao: 'Tarifa de recebimento de boleto liquidado',
  },
  {
    codigo: 'TARIFA_RECEBIMENTO_PIX',
    descricao: 'Tarifa de recebimento de Pix',
  },
  {
    codigo: 'TARIFA_RECEBIMENTO_PIX_DEVOLUCAO',
    descricao: 'Tarifa de recebimento de Pix Devolução',
  },
  {
    codigo: 'TARIFA_RECEBIMENTO_TED',
    descricao: 'Tarifa de recebimento de TED',
  },
  {
    codigo: 'TARIFA_RECEBIMENTO_TEV',
    descricao: 'Tarifa de recebimento de TEV',
  },
  { codigo: 'TAXA_TED', descricao: 'Tarifa de TED' },
];

function LegendaTarifas() {
  // Comeca recolhida: e referencia de consulta, nao precisa ocupar a tela
  // o tempo todo.
  const [aberta, setAberta] = useState(false);

  return (
    <Card>
      <Header
        type="button"
        onClick={() => setAberta((prev) => !prev)}
        aria-expanded={aberta}>
        <TitleGroup>
          <HelpOutlineIcon />
          <Title>Legenda de tarifas e taxas</Title>
        </TitleGroup>
        {aberta ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Header>
      {!aberta && (
        <Subtitle>
          Clique para ver a descrição de cada código de tarifa que aparece na
          coluna “Tipo” dos relatórios.
        </Subtitle>
      )}
      {aberta && (
        <Grid>
          {LEGENDAS.map((item) => (
            <Item key={item.codigo}>
              <Code>{item.codigo}</Code>
              <Description>{item.descricao}</Description>
            </Item>
          ))}
        </Grid>
      )}
    </Card>
  );
}

export default LegendaTarifas;
